import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  DialogTitle,
} from '@mui/material';

import { BoardInput, Workspace } from '../../mainApi';
import { SpaceDialogState } from '../../types';
import { UploadUtils } from '../../utils';
import { getDefaultVoteColors } from '../../utils/voteColors';
import { PanelSection } from '../core/PanelSection';

const MIN_BOARD_COLUMNS = 2;
const MAX_BOARD_COLUMNS = 6;
const DEFAULT_BOARD_COLUMN_COUNT = 3;

type BoardColumn = NonNullable<BoardInput['columns']>[number];

const createDefaultBoardColumns = (count = DEFAULT_BOARD_COLUMN_COUNT): BoardColumn[] =>
  Array.from({ length: count }, (_value, index) => ({
    id: `column-${index + 1}`,
    title: `Column ${index + 1}`,
    sortNo: index + 1,
  }));

const normalizeBoardColumns = (columns: BoardInput['columns'] | undefined): BoardColumn[] => {
  const normalizedColumns = Array.isArray(columns)
    ? columns
        .map((column, index) => ({
          id: column.id?.trim() || `column-${index + 1}`,
          title: column.title?.trim() || `Column ${index + 1}`,
          sortNo: typeof column.sortNo === 'number' ? column.sortNo : index + 1,
        }))
        .sort((left, right) => left.sortNo - right.sortNo)
    : [];

  return normalizedColumns.length >= MIN_BOARD_COLUMNS
    ? normalizedColumns.map((column, index) => ({ ...column, sortNo: index + 1 }))
    : createDefaultBoardColumns();
};

const resizeBoardColumns = (columns: BoardColumn[], nextCount: number) => {
  const columnCount = Math.min(
    MAX_BOARD_COLUMNS,
    Math.max(
      MIN_BOARD_COLUMNS,
      Number.isFinite(nextCount) ? Math.round(nextCount) : DEFAULT_BOARD_COLUMN_COUNT
    )
  );
  const normalizedColumns = normalizeBoardColumns(columns);

  if (columnCount <= normalizedColumns.length) {
    return normalizedColumns.slice(0, columnCount).map((column, index) => ({
      ...column,
      sortNo: index + 1,
    }));
  }

  return [
    ...normalizedColumns,
    ...createDefaultBoardColumns(columnCount).slice(normalizedColumns.length),
  ].map((column, index) => ({ ...column, sortNo: index + 1 }));
};

type DotvotingDialogProps = {
  dialogState: SpaceDialogState;
  isSaving: boolean;
  workspaces: Workspace[];
  initialTitle: string;
  initialWorkspaceTitle: string;
  initialDescription: string;
  initialPassword: string;
  onClose: () => void;
  onSubmit: (payload: BoardInput) => void;
};

type DotvotingFormState = {
  workspace: string;
  title: string;
  description: string;
  votingStyle: 'dotvoting' | 'liking' | 'none';
  voteCountSelect: string;
  customVoteCount: string;
  layout: 'grid' | 'lanes' | 'columns';
  columns: BoardColumn[];
  cardSorting: 'custom' | 'automatic';
  voteIconType: 'heart' | 'dot' | 'thumb' | 'star';
  myVoteColor: string;
  theirVoteColor: string;
  uploadTypes: Array<'text' | 'image' | 'video' | 'pdf'>;
  creatorNamesMode: 'none' | 'owner' | 'all';
  randomizeCardPosition: boolean;
  giphyEnabled: boolean;
  cardsRequireApproval: boolean;
  privateVotingEnabled: boolean;
  isPublic: boolean;
  passwordProtect: boolean;
  password: string;
  authentication: 'default' | 'optional' | 'required';
  participantUploadsEnabled: boolean;
  chatEnabled: boolean;
  timerEnabled: boolean;
  participantsEnabled: boolean;
  includeWorkspaceLink: boolean;
  locked: boolean;
  votingEnabled: boolean;
  commentsEnabled: boolean;
  externalImport: boolean;
};

const getDefaultState = (): DotvotingFormState => ({
  workspace: '',
  title: '',
  description: '',
  votingStyle: 'dotvoting',
  voteCountSelect: '3',
  customVoteCount: '',
  layout: 'grid',
  columns: createDefaultBoardColumns(),
  cardSorting: 'automatic',
  voteIconType: 'heart',
  ...getDefaultVoteColors('heart'),
  uploadTypes: ['text', 'image'],
  creatorNamesMode: 'none',
  randomizeCardPosition: false,
  giphyEnabled: true,
  cardsRequireApproval: false,
  privateVotingEnabled: false,
  isPublic: true,
  passwordProtect: false,
  password: '',
  authentication: 'default',
  participantUploadsEnabled: true,
  chatEnabled: true,
  timerEnabled: true,
  participantsEnabled: true,
  includeWorkspaceLink: false,
  locked: false,
  votingEnabled: true,
  commentsEnabled: true,
  externalImport: false,
});

export const DotvotingDialog = ({
  dialogState,
  isSaving,
  workspaces,
  initialTitle,
  initialWorkspaceTitle,
  initialDescription,
  initialPassword,
  onClose,
  onSubmit,
}: DotvotingDialogProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState<DotvotingFormState>(getDefaultState());
  const [titleError, setTitleError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [voteCountError, setVoteCountError] = useState('');
  const [cardTypeError, setCardTypeError] = useState('');

  const updateForm = <K extends keyof DotvotingFormState>(
    field: K,
    value: DotvotingFormState[K]
  ) => {
    setForm((state) => ({ ...state, [field]: value }));
  };

  const updateVoteIconType = (voteIconType: DotvotingFormState['voteIconType']) => {
    setForm((state) => ({
      ...state,
      voteIconType,
      ...getDefaultVoteColors(voteIconType),
    }));
  };

  useEffect(() => {
    if (!dialogState.open) {
      return;
    }

    const source = dialogState.space;
    const isCreateMode = dialogState.mode === 'create';
    const initialVotes = source?.votesPerUser as number | undefined;
    const sourceVoteIconType =
      (source?.voteIconType as DotvotingFormState['voteIconType'] | undefined) ?? 'heart';
    const initialVoteIconType = isCreateMode ? 'heart' : sourceVoteIconType;
    const defaultVoteColors = getDefaultVoteColors(initialVoteIconType);

    setActiveTab(0);
    setTitleError('');
    setPasswordError('');
    setVoteCountError('');
    setCardTypeError('');
    setForm({
      workspace: initialWorkspaceTitle,
      title: initialTitle,
      description: initialDescription,
      votingStyle:
        (source?.votingStyle as DotvotingFormState['votingStyle'] | undefined) ?? 'dotvoting',
      voteCountSelect:
        initialVotes && initialVotes >= 1 && initialVotes <= 10 ? String(initialVotes) : '3',
      customVoteCount:
        initialVotes && (initialVotes < 1 || initialVotes > 10) ? String(initialVotes) : '',
      layout: (source?.layout as 'grid' | 'lanes' | 'columns' | undefined) ?? 'grid',
      columns: normalizeBoardColumns(source?.columns as BoardInput['columns'] | undefined),
      cardSorting: (source?.cardSorting as 'custom' | 'automatic' | undefined) ?? 'automatic',
      voteIconType: initialVoteIconType,
      myVoteColor: isCreateMode
        ? defaultVoteColors.myVoteColor
        : (source?.myVoteColor as string | undefined) ?? defaultVoteColors.myVoteColor,
      theirVoteColor: isCreateMode
        ? defaultVoteColors.theirVoteColor
        : (source?.theirVoteColor as string | undefined) ?? defaultVoteColors.theirVoteColor,
      uploadTypes: UploadUtils.normalizeUploadTypes(
        source?.uploadTypes as string | Array<'text' | 'image' | 'video' | 'pdf'> | undefined
      ),
      creatorNamesMode: (source?.allViewParticipantNames as boolean | undefined)
        ? 'all'
        : (source?.viewParticipantNames as boolean | undefined)
        ? 'owner'
        : 'none',
      randomizeCardPosition: (source?.randomizeCardPosition as boolean | undefined) ?? false,
      giphyEnabled: (source?.giphyEnabled as boolean | undefined) ?? true,
      cardsRequireApproval: (source?.cardsRequireApproval as boolean | undefined) ?? false,
      privateVotingEnabled: (source?.privateVotingEnabled as boolean | undefined) ?? false,
      isPublic: (source?.isPublic as boolean | undefined) ?? true,
      passwordProtect: Boolean(initialPassword),
      password: initialPassword,
      authentication:
        (source?.authentication as 'default' | 'optional' | 'required' | undefined) ?? 'default',
      participantUploadsEnabled: (source?.participantUploadsEnabled as boolean | undefined) ?? true,
      chatEnabled: (source?.chatEnabled as boolean | undefined) ?? true,
      timerEnabled: (source?.timerEnabled as boolean | undefined) ?? true,
      participantsEnabled: (source?.participantsEnabled as boolean | undefined) ?? true,
      includeWorkspaceLink: (source?.includeWorkspaceLink as boolean | undefined) ?? false,
      locked: (source?.locked as boolean | undefined) ?? false,
      votingEnabled: (source?.votingEnabled as boolean | undefined) ?? true,
      commentsEnabled: (source?.commentsEnabled as boolean | undefined) ?? true,
      externalImport: (source?.externalImport as boolean | undefined) ?? false,
    });
  }, [
    dialogState.open,
    initialTitle,
    initialWorkspaceTitle,
    initialDescription,
    initialPassword,
    dialogState.mode,
    dialogState.space,
  ]);

  const selectedWorkspace = workspaces.find((workspace) => workspace.title === form.workspace);
  const workspaceLinkVisible = Boolean(selectedWorkspace?.externallyVisible);

  const validateForm = () => {
    let valid = true;

    setTitleError('');
    setPasswordError('');
    setVoteCountError('');
    setCardTypeError('');

    if (!form.title.trim()) {
      setTitleError('Required');
      valid = false;
    }

    if (form.passwordProtect && !form.password.trim()) {
      setPasswordError('Required');
      valid = false;
    }

    if (form.votingStyle === 'dotvoting') {
      const voteCount =
        form.voteCountSelect === '-1'
          ? Number.parseInt(form.customVoteCount, 10)
          : Number.parseInt(form.voteCountSelect, 10);

      if (!Number.isFinite(voteCount) || voteCount <= 0) {
        setVoteCountError('Required');
        valid = false;
      }
    }

    if (form.uploadTypes.length === 0) {
      setCardTypeError('At least one card type is required');
      valid = false;
    }

    return valid;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const votesPerUser =
      form.votingStyle === 'dotvoting'
        ? form.voteCountSelect === '-1'
          ? Number.parseInt(form.customVoteCount, 10)
          : Number.parseInt(form.voteCountSelect, 10)
        : 0;

    onSubmit({
      workspace: form.workspace.trim(),
      title: form.title.trim(),
      description: form.description.trim(),
      password: form.passwordProtect ? form.password.trim() : null,
      isPublic: form.isPublic,
      votingStyle: form.votingStyle,
      votesPerUser,
      layout: form.layout,
      columns: form.layout === 'columns' ? form.columns : [],
      cardSorting: form.cardSorting,
      voteIconType: form.voteIconType,
      myVoteColor: form.myVoteColor,
      theirVoteColor: form.theirVoteColor,
      uploadTypes: form.uploadTypes,
      randomizeCardPosition: form.randomizeCardPosition,
      giphyEnabled: form.giphyEnabled,
      cardsRequireApproval: form.cardsRequireApproval,
      viewParticipantNames: form.creatorNamesMode === 'owner',
      allViewParticipantNames: form.creatorNamesMode === 'all',
      authentication: form.authentication,
      chatEnabled: form.chatEnabled,
      timerEnabled: form.timerEnabled,
      participantsEnabled: form.participantsEnabled,
      participantUploadsEnabled: form.participantUploadsEnabled,
      includeWorkspaceLink: workspaceLinkVisible ? form.includeWorkspaceLink : false,
      locked: form.locked,
      votingEnabled: form.votingStyle === 'dotvoting' ? form.votingEnabled : true,
      privateVotingEnabled: form.votingStyle === 'dotvoting' ? form.privateVotingEnabled : false,
      commentsEnabled: form.commentsEnabled,
      externalImport: form.externalImport,
    });
  };

  return (
    <Dialog open={dialogState.open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ px: 1, pt: 1 }}>
        <Tabs value={activeTab} onChange={(_, value: number) => setActiveTab(value)}>
          <Tab
            label={
              dialogState.mode === 'create'
                ? 'New board'
                : dialogState.mode === 'edit'
                ? 'Edit board'
                : 'Copy board'
            }
          />
          <Tab label="Additional" />
          <Tab label="Card options" />
        </Tabs>
      </DialogTitle>
      <DialogContent>
        <Stack mt={1} spacing={2.5}>
          {activeTab === 0 && (
            <>
              <PanelSection title="Details">
                <FormControl size="small" fullWidth>
                  <InputLabel id="space-workspace-label">Workspace (optional)</InputLabel>
                  <Select
                    labelId="space-workspace-label"
                    value={form.workspace}
                    label="Workspace (optional)"
                    onChange={(event) => updateForm('workspace', event.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {workspaces.map((workspace) => (
                      <MenuItem key={workspace.id} value={workspace.title || ''}>
                        {workspace.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {workspaceLinkVisible && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.includeWorkspaceLink}
                        onChange={(event) =>
                          updateForm('includeWorkspaceLink', event.target.checked)
                        }
                      />
                    }
                    label="Workspace link visible in board"
                  />
                )}
                <TextField
                  label="Title"
                  value={form.title}
                  onChange={(event) => updateForm('title', event.target.value)}
                  error={Boolean(titleError)}
                  helperText={titleError}
                  fullWidth
                />
                <TextField
                  label="Description (optional)"
                  value={form.description}
                  onChange={(event) => updateForm('description', event.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </PanelSection>

              <PanelSection title="Board Setup">
                <Stack direction="column" spacing={2} pt={0.5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="layout-label">Board layout</InputLabel>
                    <Select
                      labelId="layout-label"
                      value={form.layout}
                      label="Board layout"
                      onChange={(event) =>
                        updateForm('layout', event.target.value as DotvotingFormState['layout'])
                      }
                    >
                      <MenuItem value="grid">Grid</MenuItem>
                      <MenuItem value="lanes">Lanes</MenuItem>
                      <MenuItem value="columns">Columns</MenuItem>
                    </Select>
                  </FormControl>
                  {form.layout === 'columns' && (
                    <TextField
                      type="number"
                      label="Number of columns"
                      value={form.columns.length}
                      onChange={(event) =>
                        updateForm(
                          'columns',
                          resizeBoardColumns(form.columns, Number.parseInt(event.target.value, 10))
                        )
                      }
                      inputProps={{ min: MIN_BOARD_COLUMNS, max: MAX_BOARD_COLUMNS }}
                      size="small"
                      fullWidth
                    />
                  )}

                  <FormControl size="small" fullWidth>
                    <InputLabel id="sorting-label">Card sorting</InputLabel>
                    <Select
                      labelId="sorting-label"
                      value={form.cardSorting}
                      label="Card sorting"
                      onChange={(event) =>
                        updateForm(
                          'cardSorting',
                          event.target.value as DotvotingFormState['cardSorting']
                        )
                      }
                    >
                      <MenuItem value="custom">Custom</MenuItem>
                      <MenuItem value="automatic">Automatic</MenuItem>
                    </Select>
                  </FormControl>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <FormControl size="small" fullWidth>
                      <InputLabel id="voting-style-label">Voting style</InputLabel>
                      <Select
                        labelId="voting-style-label"
                        value={form.votingStyle}
                        label="Voting style"
                        onChange={(event) => {
                          const nextVotingStyle = event.target
                            .value as DotvotingFormState['votingStyle'];
                          setForm((state) => ({
                            ...state,
                            votingStyle: nextVotingStyle,
                            votingEnabled:
                              nextVotingStyle === 'dotvoting' ? state.votingEnabled : true,
                            privateVotingEnabled:
                              nextVotingStyle === 'dotvoting'
                                ? state.privateVotingEnabled
                                : false,
                          }));
                        }}
                      >
                        <MenuItem value="dotvoting">Dot-voting</MenuItem>
                        <MenuItem value="liking">Liking</MenuItem>
                        <MenuItem value="none">None</MenuItem>
                      </Select>
                    </FormControl>

                    {form.votingStyle === 'dotvoting' && (
                      <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
                        <FormControl
                          size="small"
                          sx={{
                            minWidth: 200,
                            flex: form.voteCountSelect === '-1' ? '0 0 auto' : 1,
                          }}
                        >
                          <InputLabel id="vote-count-select-label">Votes per person</InputLabel>
                          <Select
                            labelId="vote-count-select-label"
                            value={form.voteCountSelect}
                            label="Votes per person"
                            onChange={(event) => updateForm('voteCountSelect', event.target.value)}
                          >
                            {Array.from({ length: 10 }, (_, index) => (
                              <MenuItem key={index + 1} value={String(index + 1)}>
                                {index + 1}
                              </MenuItem>
                            ))}
                            <MenuItem value="-1">Custom</MenuItem>
                          </Select>
                        </FormControl>
                        {form.voteCountSelect === '-1' && (
                          <TextField
                            label="Custom votes"
                            value={form.customVoteCount}
                            onChange={(event) => updateForm('customVoteCount', event.target.value)}
                            error={Boolean(voteCountError)}
                            helperText={voteCountError}
                            size="small"
                            sx={{ minWidth: 140, flex: 1 }}
                          />
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </PanelSection>
            </>
          )}

          {activeTab === 1 && (
            <>
              <PanelSection title="Sharing Options">
                <FormControlLabel
                  control={
                    <Switch
                      sx={{ ml: -1 }}
                      checked={form.passwordProtect}
                      onChange={(event) => updateForm('passwordProtect', event.target.checked)}
                    />
                  }
                  label="Password protected"
                  sx={{ mt: -0.5 }}
                />
                {form.passwordProtect && (
                  <Box sx={{ pb: 2 }}>
                    <TextField
                      label="Password"
                      type="password"
                      value={form.password}
                      onChange={(event) => updateForm('password', event.target.value)}
                      error={Boolean(passwordError)}
                      helperText={passwordError}
                      fullWidth
                    />
                  </Box>
                )}
                <Stack direction="column" spacing={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="creator-names-label">Card creator names</InputLabel>
                    <Select
                      labelId="creator-names-label"
                      value={form.creatorNamesMode}
                      label="Card creator names"
                      onChange={(event) =>
                        updateForm(
                          'creatorNamesMode',
                          event.target.value as DotvotingFormState['creatorNamesMode']
                        )
                      }
                    >
                      <MenuItem value="none">Not visible</MenuItem>
                      <MenuItem value="owner">Visible only to me</MenuItem>
                      <MenuItem value="all">Visible to everyone</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="auth-label">Authentication to join</InputLabel>
                    <Select
                      labelId="auth-label"
                      value={form.authentication}
                      label="Authentication to join"
                      onChange={(event) =>
                        updateForm(
                          'authentication',
                          event.target.value as DotvotingFormState['authentication']
                        )
                      }
                    >
                      <MenuItem value="default">Not required</MenuItem>
                      <MenuItem value="optional">Optional</MenuItem>
                      <MenuItem value="required">Required</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </PanelSection>

              <PanelSection title="Board Options">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.timerEnabled}
                        onChange={(event) => updateForm('timerEnabled', event.target.checked)}
                      />
                    }
                    label="Timers enabled"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.participantUploadsEnabled}
                        onChange={(event) =>
                          updateForm('participantUploadsEnabled', event.target.checked)
                        }
                      />
                    }
                    label="Participant uploads enabled"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.chatEnabled}
                        onChange={(event) => updateForm('chatEnabled', event.target.checked)}
                      />
                    }
                    label="Chat enabled"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.participantsEnabled}
                        onChange={(event) =>
                          updateForm('participantsEnabled', event.target.checked)
                        }
                      />
                    }
                    label="Participants visible"
                  />
                </FormGroup>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.locked}
                        onChange={(event) => updateForm('locked', event.target.checked)}
                      />
                    }
                    label="Board locked"
                  />
                  {form.votingStyle === 'dotvoting' && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!form.votingEnabled}
                          onChange={(event) => updateForm('votingEnabled', !event.target.checked)}
                        />
                      }
                      label="Voting disabled"
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!form.commentsEnabled}
                        onChange={(event) => updateForm('commentsEnabled', !event.target.checked)}
                      />
                    }
                    label="Commenting disabled"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.externalImport}
                        onChange={(event) => updateForm('externalImport', event.target.checked)}
                      />
                    }
                    label="Board available for import by other users"
                  />
                </FormGroup>
              </PanelSection>
            </>
          )}

          {activeTab === 2 && (
            <>
              <PanelSection title="Voting Options">
                <Stack direction="column" spacing={2} pt={1} alignItems="flex-start">
                  <FormControl size="small" fullWidth>
                    <InputLabel id="vote-icon-label">Vote icon</InputLabel>
                    <Select
                      labelId="vote-icon-label"
                      value={form.voteIconType}
                      label="Vote Icon"
                      onChange={(event) =>
                        updateVoteIconType(event.target.value as DotvotingFormState['voteIconType'])
                      }
                    >
                      <MenuItem value="heart">Heart</MenuItem>
                      <MenuItem value="dot">Dot</MenuItem>
                      <MenuItem value="thumb">Thumb</MenuItem>
                      <MenuItem value="star">Star</MenuItem>
                    </Select>
                  </FormControl>

                  <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                    <TextField
                      label="My vote color"
                      type="color"
                      value={form.myVoteColor}
                      onChange={(event) => updateForm('myVoteColor', event.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Other people's vote color"
                      type="color"
                      value={form.theirVoteColor}
                      onChange={(event) => updateForm('theirVoteColor', event.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Stack>
                </Stack>
              </PanelSection>

              <PanelSection title="Allowed Card Types">
                {cardTypeError && <Alert severity="error">{cardTypeError}</Alert>}
                <FormGroup>
                  {(['text', 'image', 'video', 'pdf'] as const).map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Switch
                          checked={form.uploadTypes.includes(type)}
                          onChange={(event) => {
                            const next = event.target.checked
                              ? [...new Set([...form.uploadTypes, type])]
                              : form.uploadTypes.filter((item) => item !== type);
                            updateForm('uploadTypes', next);
                          }}
                        />
                      }
                      label={`${type.substring(0, 1).toUpperCase() + type.substring(1)} cards`}
                    />
                  ))}
                </FormGroup>
              </PanelSection>

              <PanelSection title="Content Options">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.randomizeCardPosition}
                        onChange={(event) =>
                          updateForm('randomizeCardPosition', event.target.checked)
                        }
                      />
                    }
                    label="Cards are randomized on board loading"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.giphyEnabled}
                        onChange={(event) => updateForm('giphyEnabled', event.target.checked)}
                      />
                    }
                    label="GIFs enabled"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.cardsRequireApproval}
                        onChange={(event) =>
                          updateForm('cardsRequireApproval', event.target.checked)
                        }
                      />
                    }
                    label="Cards require approval"
                  />
                  {form.votingStyle === 'dotvoting' && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.privateVotingEnabled}
                          onChange={(event) =>
                            updateForm('privateVotingEnabled', event.target.checked)
                          }
                        />
                      }
                      label="Private voting enabled"
                    />
                  )}
                </FormGroup>
              </PanelSection>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isSaving} variant="contained" color="primary">
          {dialogState.mode === 'edit' ? 'Update' : dialogState.mode === 'copy' ? 'Copy' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
